import { ListModelsApiV1ModelsCatalogGetQueryParams } from "../../../../vl-api/vlComponents";
import { ModelCatalogFilterOptionsResponse } from "../../../../vl-api/vlSchemas";
import React, { useEffect, useMemo } from "react";
import { useNavigate, NavigateFunction } from "react-router-dom";
import { AvailableFilter } from "views/uikit/VLTable";
import { PAGE_PARAMETER } from "helpers/constants/miscellaneous";

const createFilterOptions = (items: string[] = []) =>
  items.map((item) => ({
    text: item,
    value: item,
  }));

const parseFiltersFromURL = (
  search: string,
): Record<string, string[] | string> => {
  const params = new URLSearchParams(search);
  const filters: Record<string, string[] | string> = {};
  const paramKeys = Array.from(new Set(params.keys()));
  const singleValueParams = ["sort_by", "sort_order"];

  paramKeys.forEach((key) => {
    const values = params.getAll(key).filter(Boolean);
    if (values.length > 0) {
      // Handle single-value parameters like sort_by and sort_order
      if (singleValueParams.includes(key)) {
        filters[key] = values[0];
      } else {
        filters[key] = values;
      }
    }
  });
  return filters;
};

const buildURLSearchParams = (
  filters: Record<string, any>,
  resetPage: boolean,
): URLSearchParams => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, values]) => {
    if (key === PAGE_PARAMETER && resetPage) {
      params.set(PAGE_PARAMETER, "1");
      return;
    }
    if (values !== undefined && values !== null && values !== "") {
      if (Array.isArray(values) && values.length > 0) {
        values.forEach((value) => {
          params.append(key, value);
        });
      } else if (typeof values === "string") {
        params.set(key, values);
      }
    }
  });

  return params;
};

export const updateURLWithFilters = (
  navigate: NavigateFunction,
  filters: ListModelsApiV1ModelsCatalogGetQueryParams,
  resetPage: boolean = true,
) => {
  const params = buildURLSearchParams(filters, resetPage);
  const search = params.toString();
  navigate(`/models-catalog${search ? `?${search}` : ""}`, { replace: true });
};

export default function useModelFilters(
  locationSearch: string,
  filterOptions?: ModelCatalogFilterOptionsResponse,
) {
  const [filters, setFilters] = React.useState<Record<string, any>>({});
  const navigate = useNavigate();

  useEffect(() => {
    const parsedFilters = parseFiltersFromURL(locationSearch);
    let needsURLUpdate = false;

    // Set default sort if not present
    if (!parsedFilters.sort_by) {
      parsedFilters.sort_by = "last_updated";
      needsURLUpdate = true;
    }
    if (!parsedFilters.sort_order) {
      parsedFilters.sort_order = "desc";
      needsURLUpdate = true;
    }

    if (needsURLUpdate) {
      const params = buildURLSearchParams(parsedFilters, false);
      const search = params.toString();
      navigate(`/models-catalog${search ? `?${search}` : ""}`, {
        replace: true,
      });
    }

    setFilters(parsedFilters);
  }, [locationSearch, navigate]);

  const availableFilters: AvailableFilter[] = useMemo(() => {
    const baseFilters: AvailableFilter[] = [
      {
        key: "model_name",
        title: "Model Name",
        options: createFilterOptions(filterOptions?.model_names),
        searchable: true,
      },
      {
        key: "type",
        title: "Type",
        options: createFilterOptions(filterOptions?.types),
        searchable: true,
      },
      {
        key: "source",
        title: "Source",
        options: createFilterOptions(filterOptions?.sources),
        searchable: true,
      },
      {
        key: "dataset_name",
        title: "Dataset Name",
        options: createFilterOptions(filterOptions?.dataset_names),
        searchable: true,
      },
      {
        key: "dataset_id",
        title: "Dataset ID",
        options: createFilterOptions(filterOptions?.dataset_ids),
        searchable: true,
      },
      {
        key: "job_name",
        title: "Job",
        options: createFilterOptions(filterOptions?.job_names),
        searchable: true,
      },
      {
        key: "setup_name",
        title: "Setup",
        options: createFilterOptions(filterOptions?.setup_names),
        searchable: true,
      },
      {
        key: "recipes",
        title: "Recipe List",
        options: createFilterOptions(filterOptions?.recipes),
        searchable: true,
      },
      {
        key: "tool_list",
        title: "Tool List",
        options: createFilterOptions(filterOptions?.tool_list),
        searchable: true,
      },
      {
        key: "is_production",
        title: "Production",
        options: [
          { text: "Yes", value: "true" },
          { text: "No", value: "false" },
        ],
        searchable: false,
      },
    ];

    return baseFilters;
  }, [filterOptions]);

  const queryParams = React.useMemo(() => {
    const params: Record<string, any> = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        if (key === "is_production") {
          // Convert string to boolean
          params[key] = value[0] === "true";
        } else {
          params[key] = value.length === 1 ? value[0] : value;
        }
      } else if (typeof value === "string" && value !== "") {
        // Handle single-value params like sort_by and sort_order
        params[key] = value;
      }
    });

    return params as ListModelsApiV1ModelsCatalogGetQueryParams;
  }, [filters]);

  return { filters, setFilters, queryParams, availableFilters };
}
